/**
 * The Deliverables section's setup: a gear beside the heading, opening a
 * window that refreshes this team's deliverables from its dataset
 * (shipcast-o7j.5).
 *
 * The gear carries the last refresh's outcome — "Refreshing…", "Refresh
 * failed", "N warnings" — until the next refresh, so a problem stays visible
 * after the window is closed (Q4). The window's content is the shared
 * {@link TeamRefreshPanel}; the Throughput setup window uses the same one.
 */

import { Dialog } from '@/components/dialog';

import { useTrackerCapabilities } from '../hooks';
import { useSetupWindow } from './setupWindow';
import { TeamRefreshPanel } from './TeamRefreshPanel';
import { SetupButton } from './SetupButton';
import { useTeamRefresh } from './teamRefresh';

export interface DeliverablesSetupProps {
  /** The team's server id; `undefined` until the team is first saved. */
  teamId: string | undefined;
  /** Reload the form's copy of the team once a refresh applied new data. */
  onApplied?: () => void;
}

export function DeliverablesSetup({ teamId, onApplied }: DeliverablesSetupProps) {
  const { data: capabilities } = useTrackerCapabilities();
  if (!capabilities?.licensed || !capabilities.configured || !teamId) return null;
  return <Setup teamId={teamId} onApplied={onApplied} />;
}

function Setup({ teamId, onApplied }: { teamId: string; onApplied?: () => void }) {
  // Not `useState`: see ./setupWindow — the window must outlive the remount
  // its own refresh causes.
  const [open, setOpen] = useSetupWindow(teamId, 'deliverables');
  const refresh = useTeamRefresh(teamId, 'deliverables', onApplied);

  return (
    <>
      <SetupButton refresh={refresh} label="Deliverables setup" onClick={() => setOpen(true)} />
      {open && (
        <Dialog title="Deliverables setup" onClose={() => setOpen(false)} maxWidth="max-w-lg">
          <TeamRefreshPanel refresh={refresh} />
        </Dialog>
      )}
    </>
  );
}
